import { AxiosInstance } from 'axios'
import { Iata } from '@reward-platform/platform-config'
import { Channel, Location as LocationApiResponse } from '@reward-platform/locations-api-client'

import { LocationsSearchApiParams, LocationsSearchApiHeaders } from './schemas/location.api'
import {
  LocationSearchParams,
  AncillariesLocation,
  AncillariesLocationSchema,
  CarHireLocation,
  CarHireLocationSchema,
} from './schemas/location.dto'
import { createClient } from './location.client'
import {
  formatAddressString,
  formatLocationWithCountryCode,
  formatOpeningTimes,
  getLanguageCodeFromLocale,
} from './location.utils'

type ResponseWithStatus<T> = {
  data: T
  status: number
}

const DEFAULT_PARAMS = {
  // This will always be web browser for the reward platform online
  channel: Channel.WebBrowser,
}

export class LocationService {
  client: AxiosInstance

  readonly defaultParams: Pick<LocationsSearchApiParams, 'partner' | 'channel'>

  constructor(client: AxiosInstance, opCo: Iata) {
    this.client = client
    this.defaultParams = { ...DEFAULT_PARAMS, partner: opCo }
  }

  public static async create(headers: LocationsSearchApiHeaders): Promise<LocationService> {
    const client = await createClient(headers)
    return new LocationService(client, headers.opCo)
  }

  private async requestSearchLocations(
    params: LocationsSearchApiParams
  ): Promise<ResponseWithStatus<LocationApiResponse[]>> {
    if (!this.client) {
      throw new Error('Client not initialized')
    }

    const { languageCode, vendorBrandId, multiBrand, ...rest } = params
    const { data, status } = await this.client.get('/locations/search', {
      params: {
        ...rest,
        languageCode: getLanguageCodeFromLocale(languageCode),
        // The locations API names the car hire brand filter brandId, matching the field it filters.
        ...(vendorBrandId ? { brandId: vendorBrandId } : {}),
        ...(multiBrand ? { multiBrand: true } : {}),
      },
    })
    return { data, status }
  }

  public async searchHotelLocationsByString({
    searchString,
    latitude,
    longitude,
    locale,
  }: Omit<LocationSearchParams, 'vendorBrandId'>): Promise<
    ResponseWithStatus<Array<AncillariesLocation>>
  > {
    const params: LocationsSearchApiParams = {
      ...this.defaultParams,
      searchType: 'HOTEL',
      searchString,
      latitude: latitude?.toString(),
      longitude: longitude?.toString(),
      languageCode: locale,
    }
    const { data, status } = await this.requestSearchLocations(params)
    return { data: AncillariesLocationSchema.array().parse(data), status }
  }

  public async searchCarHireLocationsByString({
    searchString,
    locale,
    vendorBrandId,
    multiBrand,
  }: LocationSearchParams): Promise<ResponseWithStatus<Array<CarHireLocation>>> {
    const params: LocationsSearchApiParams = {
      ...this.defaultParams,
      searchType: 'CAR_HIRE',
      searchString,
      languageCode: locale,
      vendorBrandId,
      multiBrand,
    }
    const { data, status } = await this.requestSearchLocations(params)
    const results = data.map((res) => {
      return {
        ...res,
        address: formatAddressString(res.address),
        vendorBrandId: res.brandId,
        openingTimes: res.openingTimes && formatOpeningTimes(res.openingTimes),
        fullName: formatLocationWithCountryCode(res.fullName, res.countryCode),
        vendors: res.vendors?.map((vendor) => ({
          ...vendor,
          address: formatAddressString(vendor.address),
          openingTimes: vendor.openingTimes && formatOpeningTimes(vendor.openingTimes),
        })),
      }
    })
    return { data: CarHireLocationSchema.array().parse(results), status }
  }

  public async searchExperienceLocationsByString({
    searchString,
    locale,
  }: Omit<LocationSearchParams, 'vendorBrandId'>): Promise<
    ResponseWithStatus<Array<AncillariesLocation>>
  > {
    const params: LocationsSearchApiParams = {
      ...this.defaultParams,
      searchType: 'EXPERIENCE',
      searchString,
      languageCode: locale,
    }
    const { data, status } = await this.requestSearchLocations(params)
    const results = data.map((res) => {
      return {
        ...res,
        fullName: formatLocationWithCountryCode(res.fullName, res.countryCode),
      }
    })
    return { data: AncillariesLocationSchema.array().parse(results), status }
  }
}
